import React, { useContext, useEffect, useState } from 'react';
import DataContext from '../../Context';
import LocalizationContext from '../../Localization/LanguageContext';
import { ILocalizationContext } from '../../Localization/Models/ILocalizationContext';
import { IPolicy } from '../../Models/PolicyInfo';
import FormRow from '../FormRow';
import Label from '../Label';
import PasswordInput from "./PasswordInput";
import { PasswordStatus } from "./validatePasswordTypes";
import {
  CalculatePasswordStatus,
  IsPasswordStrong,
  ErrorExists,
  ValidationChecklist
} from "./ValidatePassword";

interface Props {
  setPasswordIsValid: React.Dispatch<React.SetStateAction<boolean>>;
  required: boolean;
  customValidation?: boolean;
}

const Password = (props: Props) => {
  const {
    username,
    email,
    firstName,
    lastName,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword
  } = useContext(DataContext) as IPolicy;
  const { getValue, getComponent } = useContext(LocalizationContext) as ILocalizationContext;

  const [isNewDirty, setIsNewDirty] = useState(false);
  const [isConfirmDirty, setIsConfirmDirty] = useState(false);
  const [newPasswordStatus, setNewPasswordStatus] = useState(PasswordStatus.VALID);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [confirmPasswordRequired, setConfirmPasswordRequired] = useState(false);
  const [showNewPasswordValues, setShowNewPasswordValues] = useState(false);

  useEffect(() => {
    const status = CalculatePasswordStatus(
      {
        firstName: firstName,
        lastName: lastName,
        email: email,
        userName: username
      },
      password,
      confirmPassword
    )
    setNewPasswordStatus(
      status
    );

    const dirtyNew = password.length > 0;
    const dirtyConfirm = confirmPassword.length > 0;
    setIsNewDirty(dirtyNew);
    setIsConfirmDirty(dirtyConfirm);

    props.setPasswordIsValid(
      dirtyNew &&
      dirtyConfirm &&
      status === PasswordStatus.VALID
    );
  }, [username, email, firstName, lastName, password, confirmPassword]);

  const NewPasswordErrorMessage = (): string => {
    let message = "";

    if (newPasswordStatus & PasswordStatus.INVALID_CONTAINS_PROFILE_INFO)
      message = getValue('Password', 'ValNameRequirement');
    else if (!IsPasswordStrong(newPasswordStatus))
      message = getValue('Password', 'ValPasswordRequired');

    return message;
  };

  return (
    <>
      <FormRow>
        <Label htmlFor="new-password">
          {getValue('Password', 'PasswordLabel')}
        </Label>
        <PasswordInput
          id="new-password"
          errorMessage={NewPasswordErrorMessage()}
          handleBlur={() => setNewPasswordRequired(true)}
          handleFocus={() => {
            setNewPasswordRequired(false);
          }}
          isInvalid={
            newPasswordRequired &&
            newPasswordStatus != PasswordStatus.VALID &&
            newPasswordStatus != PasswordStatus.INVALID_NOT_CONFIRMED
          }
          password={password}
          setPassword={setPassword}
          showPassword={showNewPasswordValues}
          setShowPassword={setShowNewPasswordValues}
        />
      </FormRow>
      <FormRow>
        <Label htmlFor="confirm-new-password">
          {getValue('Password', 'RetypeLabel')}
        </Label>
        <PasswordInput
          id="confirm-new-password"
          errorMessage={
            ErrorExists(
              newPasswordStatus,
              PasswordStatus.INVALID_NOT_CONFIRMED
            )
              ? getValue("Password", "ValConfMatchingRequirement")
              : ""
          }
          handleBlur={() => setConfirmPasswordRequired(true)}
          handleFocus={() => {
            setConfirmPasswordRequired(false);
          }}
          isInvalid={
            confirmPasswordRequired &&
            ErrorExists(newPasswordStatus, PasswordStatus.INVALID_NOT_CONFIRMED)
          }
          password={confirmPassword}
          setPassword={setConfirmPassword}
          showPassword={showNewPasswordValues}
          setShowPassword={setShowNewPasswordValues}
        />
      </FormRow>
      <ValidationChecklist
        newPasswordStatus={newPasswordStatus}
        isNewDirty={isNewDirty}
        isConfirmDirty={isConfirmDirty}
        passwordResources={getComponent("Password")}
      />
    </>
  );
};

export default Password;
